import { RedoCustomerWidgetRpcClient } from "@redotech/chat-widget-sdk/customer-widget-rpc/client";
import {
  AuthorizationField,
  credentialsFieldValueFormat,
} from "@redotech/http/semantics";
import { bearerCredentialsFormat } from "@redotech/oauth2/request";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { setLocalStorageJson } from "@redotech/redo-web/utils/local-storage-wrapper";
import { CUSTOMER_WIDGET_TOKEN_KEY } from "@redotech/redo-web/utils/shared-conf";
import {
  getCustomerWidgetAuthToken,
  parseAuth,
} from "@redotech/redo-web/utils/storefront-is-logged-in";
import { useQueryClient } from "@tanstack/react-query";
import jwtDecode from "jwt-decode";
import {
  createContext,
  memo,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useCurrentConversationId } from "../hooks/conversation-hooks";
import { TEMP_CONVERSATION_ID } from "../utils/add-message-utils";
import {
  clearAnonymousConversations,
  getAnonymousConversationIds,
  getAnonymousConversationToken,
  upsertAnonymousConversation,
} from "../utils/anonymous-conversations-storage";
import { RedoCustomerWidgetRpcClientContext } from "./redo-customer-widget-rpc-client";
import { TeamInfoContext } from "./team-info";

// Function to associate anonymous concierge conversations with customer after login
const associateConciergeConversationsWithCustomer = async (
  rpcClient: RedoCustomerWidgetRpcClient,
  widgetAuthToken: string,
) => {
  const conversationIds = getAnonymousConversationIds();

  if (conversationIds.length > 0) {
    await rpcClient.associateConciergeConversationsWithCustomer({
      widgetAuthToken,
      conversationIds,
    });
  }

  clearAnonymousConversations();
};

export interface Auth {
  expiration: Temporal.Instant;
  teamId: string;
  customerEmail: string;
  firstName?: string | undefined;
  token: string;
}

export interface ConversationAuth {
  setConversationAuth: (
    conversationId: string,
    conversationAuthToken: string | undefined,
  ) => void;
  getConversationAuth: (conversationId: string) => string | undefined;
  conversationToken: string | undefined;
}

export const AuthContext = createContext<Auth | undefined>(undefined);
export const ConversationAuthContext = createContext<
  ConversationAuth | undefined
>(undefined);

export type SetAuth = (auth: string | undefined) => void;

export const SetAuthContext = createContext<SetAuth | undefined>(undefined);

export const authTokenToAuthHeader = (authToken: string) => ({
  [String(AuthorizationField.name)]: credentialsFieldValueFormat.write(
    bearerCredentialsFormat.write(authToken),
  ),
});

export const AuthProvider = memo(function AuthProvider({
  children,
}: {
  children: ReactNode | ReactNode[];
}) {
  const teamInfo = useContext(TeamInfoContext);
  const rpcClient = useContext(RedoCustomerWidgetRpcClientContext);
  const queryClient = useQueryClient();
  const [conversationId] = useCurrentConversationId();

  const [auth, setAuth] = useState<Auth | undefined>(() => {
    const validToken = getCustomerWidgetAuthToken();
    return validToken ? parseAuth(validToken) : undefined;
  });

  const firstName = useFirstName({ token: auth?.token, rpcClient });

  const [currentConversationToken, setCurrentConversationToken] = useState<
    string | undefined
  >(undefined);

  const setConversationAuth = useHandler(
    (
      targetConversationId: string,
      conversationAuthToken: string | undefined,
    ) => {
      // Update the token for the current conversation in anonymous conversations storage
      if (
        conversationAuthToken &&
        targetConversationId !== TEMP_CONVERSATION_ID
      ) {
        // Check if this is a new conversation to decide if we need to invalidate queries
        const existingToken =
          getAnonymousConversationToken(targetConversationId);
        const isNewConversation = existingToken === undefined;

        upsertAnonymousConversation(
          targetConversationId,
          conversationAuthToken,
        );

        if (isNewConversation && teamInfo) {
          void queryClient.invalidateQueries({
            queryKey: ["conversations", teamInfo.id],
          });
        }
      }

      // If this is for the current active conversation, update the current token
      if (targetConversationId === conversationId) {
        setCurrentConversationToken(conversationAuthToken);
      }
    },
  );

  const getConversationAuth = useHandler((targetConversationId: string) => {
    if (targetConversationId === TEMP_CONVERSATION_ID) {
      return undefined;
    }

    return getAnonymousConversationToken(targetConversationId);
  });

  // Update current conversation token when conversation changes
  useEffect(() => {
    if (conversationId) {
      const token = getConversationAuth(conversationId);
      setCurrentConversationToken(token);
    } else {
      setCurrentConversationToken(undefined);
    }
  }, [conversationId, getConversationAuth]);

  const conversationAuth: ConversationAuth = useMemo(
    () => ({
      setConversationAuth,
      getConversationAuth,
      conversationToken: currentConversationToken,
    }),
    [setConversationAuth, getConversationAuth, currentConversationToken],
  );

  useEffect(() => {
    if (!teamInfo || !auth) {
      return;
    }
    const duration = auth.expiration.since(Temporal.Now.instant());
    if (duration.sign < 0) {
      setAuth_(undefined);
      return;
    }
    const timeout = setTimeout(
      () => setAuth_(undefined),
      Math.min(2_147_483_647, duration.total("milliseconds")),
    );
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setAuth_ is stable from useHandler
  }, [auth, teamInfo]);

  const setAuth_ = useHandler<SetAuth>((token) => {
    if (!teamInfo) {
      return;
    }
    if (token === undefined) {
      localStorage.removeItem(CUSTOMER_WIDGET_TOKEN_KEY);
      setAuth(undefined);
    } else if (!isValidTokenFormat(token)) {
      return;
    } else {
      setLocalStorageJson(CUSTOMER_WIDGET_TOKEN_KEY, token);

      const parsed = parseAuth(token);
      void onLogin(parsed);

      setAuth(parsed);
    }
  });

  const isValidTokenFormat = (token: string) => {
    try {
      const decoded = jwtDecode<any>(token);
      return (
        (decoded.teamId && decoded.customerEmail) ||
        (decoded.aud && decoded.sub)
      );
    } catch (error) {
      return false;
    }
  };

  const onLogin = async (auth: Auth) => {
    if (!rpcClient) {
      return;
    }

    await associateConciergeConversationsWithCustomer(rpcClient, auth.token);
  };

  if (!teamInfo) {
    return null;
  }

  return (
    <AuthContext.Provider value={auth ? { ...auth, firstName } : undefined}>
      <SetAuthContext.Provider value={setAuth_}>
        <ConversationAuthContext.Provider value={conversationAuth}>
          {children}
        </ConversationAuthContext.Provider>
      </SetAuthContext.Provider>
    </AuthContext.Provider>
  );
});

function useFirstName({
  token,
  rpcClient,
}: {
  token?: string | undefined;
  rpcClient?: RedoCustomerWidgetRpcClient | undefined;
}) {
  const firstName = useLoad(async () => {
    if (!token || !rpcClient) {
      return undefined;
    }

    const contextResult = await rpcClient.getCustomerContext(null, {
      requestHeaders: authTokenToAuthHeader(token),
    });
    return contextResult.firstName;
  }, [token, rpcClient]);

  return firstName.value;
}
